import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import CallTypeBadge from '../components/CallTypeBadge';
import ViewPrescriptionModal from '../components/ViewPrescriptionModal';
import FilterSelect from '../components/FilterSelect';
import { toast } from 'react-toastify';

function UserBookings() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL'); // ALL, UPCOMING, PENDING, CONFIRMED, SESSION_EXPIRED, COMPLETED_SESSION, MISSED, REJECTED, CANCELLED
  const [videoSessionStatus, setVideoSessionStatus] = useState({}); // Track which appointments have joined calls
  const [searchQuery, setSearchQuery] = useState(''); // Search filter
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Cancel modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  // Reschedule modal states
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedNewSlot, setSelectedNewSlot] = useState(null);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // Success/Error message states
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [viewPrescription, setViewPrescription] = useState(null);
  const [loadingPrescription, setLoadingPrescription] = useState(false);

  useEffect(() => {
    fetchBookings();
    // Polling removed as per user request
  }, []);

  const openPrescriptionForBooking = async (booking) => {
    setLoadingPrescription(true);
    try {
      const res = await api.get(
        `prescriptions/user/appointment/${booking.appointment_id}/`
      );
      if (res.data.prescription) {
        setViewPrescription(res.data.prescription);
      } else {
        toast.info('Doctor has not sent a prescription yet for this visit.');
      }
    } catch (err) {
      toast.error('Could not load prescription');
    } finally {
      setLoadingPrescription(false);
    }
  };

  const fetchBookings = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);

      const res = await api.get('appointments/user/my-bookings/');
      setBookings(res.data);

      // Fetch video session status for all accepted appointments
      const acceptedBookings = res.data.filter(b =>
        b.status === 'ACCEPTED' || b.status === 'CONFIRMED'
      );

      const sessionStatusMap = {};
      await Promise.all(
        acceptedBookings.map(async (booking) => {
          try {
            const sessionRes = await api.get(`video/session-info/${booking.appointment_id}/`);
            sessionStatusMap[booking.appointment_id] = {
              userJoined: sessionRes.data?.user_joined_at !== null && sessionRes.data?.user_joined_at !== undefined,
              doctorJoined: sessionRes.data?.doctor_joined_at !== null && sessionRes.data?.doctor_joined_at !== undefined
            };
          } catch (err) {
            // If session doesn't exist, user didn't join
            sessionStatusMap[booking.appointment_id] = {
              userJoined: false,
              doctorJoined: false
            };
          }
        })
      );
      setVideoSessionStatus(sessionStatusMap);
    } catch (err) {
      // Only redirect on auth error if it's the initial load
      if (err.response?.status === 401 && showLoading) navigate('/user/login');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Cancel appointment handler
  const handleCancelAppointment = async () => {
    if (!selectedBooking) return;

    setCancelLoading(true);
    setErrorMessage('');

    try {
      const res = await api.post('appointments/user/appointment/cancel/', {
        appointment_id: selectedBooking.appointment_id,
        reason: cancelReason
      });

      // Build success message with refund info if available
      let message = res.data.msg || 'Appointment cancelled successfully!';
      if (res.data.refund?.processed) {
        message = `Appointment cancelled! Refund of $${res.data.refund.refund_amount.toFixed(2)} processed. Processing fee of $${res.data.refund.processing_fee_kept.toFixed(2)} retained. Check your email for details.`;
      }

      setSuccessMessage(message);
      setShowCancelModal(false);
      setShowDetailModal(false);
      setCancelReason('');
      setSelectedBooking(null);
      fetchBookings(); // Refresh bookings

      // Clear success message after 8 seconds (longer for refund info)
      setTimeout(() => setSuccessMessage(''), 8000);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to cancel appointment');
    } finally {
      setCancelLoading(false);
    }
  };

  // Fetch available slots for rescheduling
  const fetchAvailableSlots = async (appointmentId) => {
    setSlotsLoading(true);
    setErrorMessage('');

    try {
      const res = await api.get(`appointments/user/appointment/${appointmentId}/available-slots/`);
      setAvailableSlots(res.data.available_slots || []);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to fetch available slots');
    } finally {
      setSlotsLoading(false);
    }
  };

  // Open reschedule modal
  const openRescheduleModal = (booking) => {
    setSelectedBooking(booking);
    setShowRescheduleModal(true);
    setSelectedNewSlot(null);
    setRescheduleReason('');
    fetchAvailableSlots(booking.appointment_id);
  };

  // Reschedule appointment handler
  const handleRescheduleAppointment = async () => {
    if (!selectedBooking || !selectedNewSlot) return;

    setRescheduleLoading(true);
    setErrorMessage('');

    try {
      const res = await api.post('appointments/user/appointment/reschedule/', {
        appointment_id: selectedBooking.appointment_id,
        new_slot_id: selectedNewSlot.id,
        reason: rescheduleReason
      });

      // Show appropriate message based on whether approval is required
      let message = res.data.msg || 'Appointment rescheduled successfully!';
      if (res.data.requires_approval) {
        message = `Appointment rescheduled to ${res.data.new_date} at ${res.data.new_time}. The doctor will need to accept your new booking request.`;
      }

      setSuccessMessage(message);
      setShowRescheduleModal(false);
      setShowDetailModal(false);
      setRescheduleReason('');
      setSelectedNewSlot(null);
      setSelectedBooking(null);
      fetchBookings(); // Refresh bookings

      // Clear success message after 7 seconds (longer for important message)
      setTimeout(() => setSuccessMessage(''), 7000);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to reschedule appointment');
    } finally {
      setRescheduleLoading(false);
    }
  };

  // Check if appointment can be cancelled (allows until the appointment time)
  const canCancelAppointment = (booking) => {
    if (booking.status === 'CANCELLED' || booking.status === 'REJECTED' || booking.status === 'MISSED') return false;

    // Create appointment datetime from date and start_time
    const appointmentDateTime = new Date(`${booking.date}T${booking.start_time}`);
    const now = new Date();

    return appointmentDateTime > now;
  };

  // Check if appointment can be rescheduled (must be at least 12 hours before)
  const canRescheduleAppointment = (booking) => {
    if (booking.status === 'CANCELLED' || booking.status === 'REJECTED' || booking.status === 'MISSED') return false;

    // Create appointment datetime from date and start_time
    const appointmentDateTime = new Date(`${booking.date}T${booking.start_time}`);
    const now = new Date();

    // Calculate hours until appointment
    const hoursUntilAppointment = (appointmentDateTime - now) / (1000 * 60 * 60);

    // Must be at least 12 hours before the appointment
    return hoursUntilAppointment >= 12;
  };

  // Legacy function for backward compatibility - checks if appointment can be modified at all
  const canModifyAppointment = (booking) => {
    return canCancelAppointment(booking);
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Format time for display
  const formatTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Helper function to get image URL
  const getDoctorImageUrl = (image) => {
    if (!image) return "https://via.placeholder.com/300x300?text=Doctor";

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }

    if (image.startsWith('/media/')) {
      return `${apiUrl}${image}`;
    }

    return `${apiUrl}/media/${image}`;
  };

  // Search filter helper function
  const filterBySearch = (bookingList) => {
    if (!searchQuery.trim()) return bookingList;

    const query = searchQuery.toLowerCase().trim();
    return bookingList.filter(b => {
      const doctorName = (b.doctor_name || '').toLowerCase();
      const date = (b.date || '').toLowerCase();
      const startTime = (b.start_time || '').toLowerCase();
      const endTime = (b.end_time || '').toLowerCase();
      const cancelledSlotTime = (b.cancelled_slot_time || '').toLowerCase();

      return doctorName.includes(query) ||
        date.includes(query) ||
        startTime.includes(query) ||
        endTime.includes(query) ||
        cancelledSlotTime.includes(query);
    });
  };

  // Filter bookings based on selected filter
  const filteredBookings = filterBySearch(bookings
    .filter((b) => {
      if (filter === 'ALL') return true;
      if (filter === 'UPCOMING') {
        // Check if status is ACCEPTED or CONFIRMED
        if (b.status !== 'ACCEPTED' && b.status !== 'CONFIRMED') return false;

        // Check if the appointment is actually in the future (using date + time)
        if (b.date && b.start_time) {
          const appointmentDateTime = new Date(`${b.date} ${b.start_time}`);
          const now = new Date();
          return appointmentDateTime >= now; // Only include future appointments
        }

        // Fallback: if no time info, check date only
        const appointmentDate = new Date(b.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return appointmentDate >= today;
      }
      if (filter === 'CONFIRMED') {
        return b.status === 'CONFIRMED' || b.status === 'ACCEPTED';
      }
      if (filter === 'SESSION_EXPIRED') {
        // Exclude completed sessions (they show under Completed Session)
        if (b.status === 'COMPLETED' || b.status === 'MISSED') return false;
        if (b.status !== 'ACCEPTED' && b.status !== 'CONFIRMED') return false;

        // Check if the appointment end time has passed
        if (b.date && b.end_time) {
          const appointmentEndDateTime = new Date(`${b.date} ${b.end_time}`);
          const now = new Date();
          const gracePeriod = 5 * 60 * 1000; // 5 minutes
          const isPast = appointmentEndDateTime.getTime() + gracePeriod < now.getTime();

          if (isPast) {
            // Check if user joined the call
            const sessionInfo = videoSessionStatus[b.appointment_id];
            return !sessionInfo?.userJoined; // User didn't join
          }
        }
        return false;
      }
      if (filter === 'COMPLETED_SESSION') {
        // Backend marks appointment as COMPLETED when session ends
        if (b.status === 'COMPLETED') return true;
        // Legacy: past ACCEPTED/CONFIRMED where user joined
        if (b.status !== 'ACCEPTED' && b.status !== 'CONFIRMED') return false;
        if (b.date && b.end_time) {
          const appointmentEndDateTime = new Date(`${b.date} ${b.end_time}`);
          const now = new Date();
          const gracePeriod = 5 * 60 * 1000; // 5 minutes
          const isPast = appointmentEndDateTime.getTime() + gracePeriod < now.getTime();
          if (isPast) {
            const sessionInfo = videoSessionStatus[b.appointment_id];
            return sessionInfo?.userJoined === true;
          }
        }
        return false;
      }
      if (filter === 'MISSED') {
        return b.status === 'MISSED';
      }
      if (filter === 'CANCELLED') {
        return b.status === 'CANCELLED';
      }
      return b.status === filter;
    })
    .sort((a, b) => {
      // Sort by date and time (newest first)
      const dateA = a.date || '';
      const dateB = b.date || '';

      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      // Combine date and time for accurate sorting
      const timeA = a.start_time || '00:00:00';
      const timeB = b.start_time || '00:00:00';

      const dateTimeA = new Date(`${dateA} ${timeA}`);
      const dateTimeB = new Date(`${dateB} ${timeB}`);

      // Sort descending (newest first)
      return dateTimeB - dateTimeA;
    }));

  // Calculate statistics
  const stats = {
    total: bookings.length,
    upcoming: bookings.filter(b => {
      // Check if status is ACCEPTED or CONFIRMED
      if (b.status !== 'ACCEPTED' && b.status !== 'CONFIRMED') return false;

      // Check if the appointment is actually in the future (using date + time)
      if (b.date && b.start_time) {
        const appointmentDateTime = new Date(`${b.date} ${b.start_time}`);
        const now = new Date();
        return appointmentDateTime >= now; // Only include future appointments
      }

      // Fallback: if no time info, check date only
      const appointmentDate = new Date(b.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return appointmentDate >= today;
    }).length,
    pending: bookings.filter(b => b.status === 'PENDING').length,
    confirmed: bookings.filter(b => b.status === 'CONFIRMED' || b.status === 'ACCEPTED').length,
    sessionExpired: bookings.filter(b => {
      if (b.status === 'COMPLETED' || b.status === 'MISSED') return false;
      if (b.status !== 'ACCEPTED' && b.status !== 'CONFIRMED') return false;
      if (b.date && b.end_time) {
        const appointmentEndDateTime = new Date(`${b.date} ${b.end_time}`);
        const now = new Date();
        const gracePeriod = 5 * 60 * 1000; // 5 minutes
        const isPast = appointmentEndDateTime.getTime() + gracePeriod < now.getTime();
        if (isPast) {
          const sessionInfo = videoSessionStatus[b.appointment_id];
          return !sessionInfo?.userJoined;
        }
      }
      return false;
    }).length,
    completedSession: bookings.filter(b => {
      if (b.status === 'COMPLETED') return true;
      if (b.status !== 'ACCEPTED' && b.status !== 'CONFIRMED') return false;
      if (b.date && b.end_time) {
        const appointmentEndDateTime = new Date(`${b.date} ${b.end_time}`);
        const now = new Date();
        const gracePeriod = 5 * 60 * 1000; // 5 minutes
        const isPast = appointmentEndDateTime.getTime() + gracePeriod < now.getTime();
        if (isPast) {
          const sessionInfo = videoSessionStatus[b.appointment_id];
          return sessionInfo?.userJoined === true;
        }
      }
      return false;
    }).length,
    missed: bookings.filter(b => b.status === 'MISSED').length,
    rejected: bookings.filter(b => b.status === 'REJECTED').length,
    cancelled: bookings.filter(b => b.status === 'CANCELLED').length,
  };

  if (loading) {
    return (
      <div className="bg-light min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      <UserNavbar />
      <div className="container py-4">

        {/* SUCCESS/ERROR ALERTS */}
        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show shadow-sm" role="alert">
            <i className="bi bi-check-circle-fill me-2"></i>
            {successMessage}
            <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
          </div>
        )}
        {errorMessage && (
          <div className="alert alert-danger alert-dismissible fade show shadow-sm" role="alert">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {errorMessage}
            <button type="button" className="btn-close" onClick={() => setErrorMessage('')}></button>
          </div>
        )}

        {/* HEADER SECTION */}
        <div className="row mb-4 align-items-center">
          <div className="col-12 col-md-8">
            <h2 className="fw-bold text-dark mb-0 fs-4 fs-md-3">
              <i className="bi bi-calendar-check me-2 text-primary"></i>My Appointments
            </h2>
            <p className="text-muted mb-0 mb-md-2">Manage your upcoming and past medical consultations.</p>
          </div>
          <div className="col-12 col-md-4 text-md-end mt-3 mt-md-0">
            <button
              className="btn btn-primary shadow-sm rounded-pill px-4"
              onClick={() => navigate('/user/doctor_list')}
            >
              <i className="bi bi-plus-circle me-2"></i>Book New Appointment
            </button>
          </div>
        </div>

        {/* STATISTICS CARDS */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body p-3">
                <div className="d-flex align-items-center">
                  <div className="bg-primary bg-opacity-4 text-primary rounded-circle p-2 me-3">
                    <i className="bi bi-calendar-event text-white fs-5"></i>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-0">{stats.total}</h6>
                    <small className="text-muted">Total</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body p-3">
                <div className="d-flex align-items-center">
                  <div className="bg-success bg-opacity-10 text-success rounded-circle p-2 me-3">
                    <i className="bi bi-check-circle fs-5"></i>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-0">{stats.upcoming}</h6>
                    <small className="text-muted">Upcoming</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body p-3">
                <div className="d-flex align-items-center">
                  <div className="bg-warning bg-opacity-10 text-warning rounded-circle p-2 me-3">
                    <i className="bi bi-clock-history fs-5"></i>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-0">{stats.pending}</h6>
                    <small className="text-muted">Pending</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body p-3">
                <div className="d-flex align-items-center">
                  <div className="bg-danger bg-opacity-10 text-danger rounded-circle p-2 me-3">
                    <i className="bi bi-x-circle fs-5"></i>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-0">{stats.rejected}</h6>
                    <small className="text-muted">Rejected</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FILTER AND SEARCH */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body p-3">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-4">
                <label className="form-label small fw-bold text-muted mb-2">Filter by Status</label>
                <FilterSelect
                  value={filter}
                  onChange={setFilter}
                  options={[
                    { value: 'ALL', label: `All (${stats.total})` },
                    { value: 'UPCOMING', label: `Upcoming (${stats.upcoming})` },
                    { value: 'PENDING', label: `Pending (${stats.pending})` },
                    { value: 'CONFIRMED', label: `Confirmed (${stats.confirmed})` },
                    { value: 'SESSION_EXPIRED', label: `Session Expired (${stats.sessionExpired})` },
                    { value: 'COMPLETED_SESSION', label: `Completed Session (${stats.completedSession})` },
                    { value: 'MISSED', label: `Missed (${stats.missed})` },
                    { value: 'REJECTED', label: `Rejected (${stats.rejected})` },
                    { value: 'CANCELLED', label: `Cancelled (${stats.cancelled})` },
                  ]}
                  placeholder="Filter by Status"
                  aria-label="Filter by Status"
                  variant="bordered"
                />
              </div>
              <div className="col-12 col-md-8">
                <label className="form-label small fw-bold text-muted mb-2">Search</label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0">
                    <i className="bi bi-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Search by doctor name, date, or time..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="btn btn-outline-secondary border-start-0"
                      type="button"
                      onClick={() => setSearchQuery('')}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* BOOKINGS LIST */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body p-0">
            {filteredBookings.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-calendar-x fs-1 text-muted mb-3 d-block"></i>
                <h5 className="fw-bold">No appointments found</h5>
                <p className="text-muted mb-4">
                  {searchQuery
                    ? 'No appointments match your search criteria.'
                    : filter === 'ALL'
                      ? "You don't have any appointments yet."
                      : `No ${filter.toLowerCase()} appointments found.`}
                </p>
                {filter === 'ALL' && !searchQuery && (
                  <button
                    className="btn btn-primary rounded-pill px-4"
                    onClick={() => navigate('/user/doctor_list')}
                  >
                    <i className="bi bi-plus-circle me-2"></i>Book Your First Appointment
                  </button>
                )}
              </div>
            ) : (
              <div className="p-3">
                {searchQuery && (
                  <div className="mb-3">
                    <span className="badge bg-primary">
                      {filteredBookings.length} appointment{filteredBookings.length !== 1 ? 's' : ''} found
                    </span>
                  </div>
                )}
                <div className="row g-3">
                  {filteredBookings.map((b) => (
                    <div key={b.appointment_id} className="col-12">
                      <div className="card border-0 shadow-sm h-100 hover-shadow">
                        <div className="card-body p-4">
                          <div className="row align-items-center">
                            {/* Doctor Info */}
                            <div className="col-12 col-md-4 mb-3 mb-md-0">
                              <div className="d-flex align-items-center">
                                <div className="bg-light rounded-circle p-2 me-3">
                                  <i className="bi bi-person-badge fs-4 text-primary"></i>
                                </div>
                                <div>
                                  <h6 className="fw-bold mb-1">{b.doctor_name}</h6>
                                  <small className="text-muted d-block">
                                    <i className="bi bi-briefcase me-1"></i>
                                    {b.doctor_specialization || 'Specialist'}
                                  </small>
                                  {(b.family_member_name || b.patient_name) && (
                                    <small className="text-primary d-block mt-1">
                                      <i className="bi bi-people me-1"></i>
                                      For{' '}
                                      {b.family_member_name
                                        ? `${b.family_member_name}${b.family_member_relation ? ` (${b.family_member_relation})` : ''}`
                                        : `${b.patient_name} (Self)`}
                                    </small>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Date & Time */}
                            <div className="col-12 col-md-3 mb-3 mb-md-0">
                              <div className="d-flex align-items-center mb-2">
                                <i className="bi bi-calendar-event text-primary me-2"></i>
                                <span className="fw-medium">{b.date || 'N/A'}</span>
                              </div>
                              <div className="d-flex align-items-center mb-2">
                                <i className="bi bi-clock text-primary me-2"></i>
                                <span className="text-primary fw-bold">
                                  {b.start_time && b.end_time
                                    ? `${b.start_time} - ${b.end_time}`
                                    : b.cancelled_slot_time || 'N/A'}
                                </span>
                              </div>
                              {b.call_type && <CallTypeBadge callType={b.call_type} />}
                            </div>

                            {/* Status */}
                            <div className="col-12 col-md-2 mb-3 mb-md-0 text-center text-md-start">
                              <span className={`badge rounded-pill px-3 py-2 ${b.status === 'COMPLETED' ? 'bg-success' :
                                b.status === 'ACCEPTED' || b.status === 'CONFIRMED' ? 'bg-success' :
                                  b.status === 'PENDING' ? 'bg-warning text-dark' :
                                    b.status === 'MISSED' ? 'bg-warning text-dark' :
                                      b.status === 'REJECTED' ? 'bg-danger' :
                                        b.status === 'CANCELLED' ? 'bg-secondary' : 'bg-secondary'
                                }`}>
                                {b.status === 'COMPLETED' ? 'Completed' : b.status === 'MISSED' ? 'Missed' : b.status}
                              </span>
                            </div>

                            {/* Action */}
                            <div className="col-12 col-md-3 text-center text-md-end">
                              <div className="d-flex gap-2 justify-content-center justify-content-md-end flex-wrap">
                                <button
                                  className="btn btn-outline-primary btn-sm rounded-pill px-3"
                                  onClick={() => {
                                    setSelectedBooking(b);
                                    setShowDetailModal(true);
                                  }}
                                >
                                  <i className="bi bi-eye me-1"></i>View
                                </button>
                                {/* Video Call Button - Only for ACCEPTED appointments */}
                                {(b.status === 'ACCEPTED' || b.status === 'CONFIRMED') && (
                                  <VideoCallButton booking={b} navigate={navigate} />
                                )}
                                {canRescheduleAppointment(b) && (
                                  <button
                                    className="btn btn-outline-warning btn-sm rounded-pill px-3"
                                    onClick={() => openRescheduleModal(b)}
                                  >
                                    <i className="bi bi-calendar-event me-1"></i>Reschedule
                                  </button>
                                )}
                                {canCancelAppointment(b) && (
                                  <button
                                    className="btn btn-outline-danger btn-sm rounded-pill px-3"
                                    onClick={() => {
                                      setSelectedBooking(b);
                                      setShowCancelModal(true);
                                    }}
                                  >
                                    <i className="bi bi-x-circle me-1"></i>Cancel
                                  </button>
                                )}
                                {b.status === 'COMPLETED' && (
                                  <button
                                    type="button"
                                    className={`btn btn-sm rounded-pill px-3 ${
                                      b.has_prescription ? 'btn-success' : 'btn-outline-secondary'
                                    }`}
                                    disabled={loadingPrescription}
                                    onClick={() => openPrescriptionForBooking(b)}
                                  >
                                    <i className="bi bi-file-medical me-1" />
                                    {b.has_prescription ? 'Prescription' : 'No Rx yet'}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* APPOINTMENT DETAIL MODAL */}
      {showDetailModal && selectedBooking && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-primary">Appointment Details</h5>
                <button type="button" className="btn-close" onClick={() => setShowDetailModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="mb-4 text-center">
                  <div className="bg-primary bg-opacity-10 text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                    <i className="bi bi-person-badge fs-2"></i>
                  </div>
                  <h5 className="fw-bold mb-0">{selectedBooking.doctor_name}</h5>
                  <p className="text-muted small">
                    {selectedBooking.doctor_specialization || 'Specialist'}
                  </p>
                </div>

                <div className="row g-3">
                  <div className="col-6">
                    <label className="text-muted small fw-bold text-uppercase d-block">Date</label>
                    <p className="fw-medium mb-0">
                      <i className="bi bi-calendar-event me-2 text-primary"></i>
                      {selectedBooking.date || 'N/A'}
                    </p>
                  </div>
                  <div className="col-6">
                    <label className="text-muted small fw-bold text-uppercase d-block">Time</label>
                    <p className="fw-medium mb-0">
                      <i className="bi bi-clock me-2 text-primary"></i>
                      {selectedBooking.start_time && selectedBooking.end_time
                        ? `${selectedBooking.start_time} - ${selectedBooking.end_time}`
                        : selectedBooking.cancelled_slot_time || 'N/A'}
                    </p>
                  </div>
                  {selectedBooking.call_type && (
                    <div className="col-6">
                      <label className="text-muted small fw-bold text-uppercase d-block">Call Type</label>
                      <CallTypeBadge callType={selectedBooking.call_type} />
                    </div>
                  )}
                  <div className="col-12">
                    <label className="text-muted small fw-bold text-uppercase d-block">Consultation For</label>
                    <p className="fw-medium mb-0">
                      <i className="bi bi-people me-2 text-primary"></i>
                      {selectedBooking.family_member_name
                        ? `${selectedBooking.family_member_name}${selectedBooking.family_member_relation ? ` (${selectedBooking.family_member_relation})` : ''}`
                        : `${selectedBooking.patient_name || 'Self'} (Self)`}
                    </p>
                  </div>
                  <div className="col-12 border-top pt-3">
                    <label className="text-muted small fw-bold text-uppercase d-block">Status</label>
                    <span className={`badge rounded-pill px-3 py-2 ${selectedBooking.status === 'COMPLETED' ? 'bg-success' :
                      selectedBooking.status === 'ACCEPTED' || selectedBooking.status === 'CONFIRMED' ? 'bg-success' :
                        selectedBooking.status === 'PENDING' ? 'bg-warning text-dark' :
                          selectedBooking.status === 'MISSED' ? 'bg-warning text-dark' :
                            selectedBooking.status === 'REJECTED' ? 'bg-danger' :
                              selectedBooking.status === 'CANCELLED' ? 'bg-secondary' : 'bg-secondary'
                      }`}>
                      {selectedBooking.status === 'COMPLETED' ? 'Completed' : selectedBooking.status === 'MISSED' ? 'Missed' : selectedBooking.status}
                    </span>
                  </div>
                  {selectedBooking.rejection_reason && (
                    <div className="col-12 border-top pt-3">
                      <label className="text-muted small fw-bold text-uppercase d-block">
                        {selectedBooking.status === 'CANCELLED'
                          ? 'Cancellation Reason'
                          : selectedBooking.status === 'MISSED'
                            ? 'Missed Reason'
                            : 'Rejection Reason'}
                      </label>
                      <p className="mb-0 text-muted">{selectedBooking.rejection_reason}</p>
                    </div>
                  )}
                  {selectedBooking.rescheduling_time && (
                    <div className="col-12 border-top pt-3">
                      <label className="text-muted small fw-bold text-uppercase d-block">Rescheduling Info</label>
                      <p className="mb-0 text-muted">{selectedBooking.rescheduling_time}</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="mt-4">
                  {(canRescheduleAppointment(selectedBooking) || canCancelAppointment(selectedBooking)) && (
                    <div className="d-flex gap-2 mb-3">
                      {canRescheduleAppointment(selectedBooking) && (
                        <button
                          className="btn btn-warning flex-fill rounded-3 fw-bold"
                          onClick={() => {
                            setShowDetailModal(false);
                            openRescheduleModal(selectedBooking);
                          }}
                        >
                          <i className="bi bi-calendar-event me-2"></i>Reschedule
                        </button>
                      )}
                      {canCancelAppointment(selectedBooking) && (
                        <button
                          className="btn btn-danger flex-fill rounded-3 fw-bold"
                          onClick={() => {
                            setShowDetailModal(false);
                            setShowCancelModal(true);
                          }}
                        >
                          <i className="bi bi-x-circle me-2"></i>Cancel
                        </button>
                      )}
                    </div>
                  )}
                  {selectedBooking.status === 'COMPLETED' && (
                    <button
                      type="button"
                      className="btn btn-success w-100 rounded-3 fw-bold mb-2"
                      disabled={loadingPrescription}
                      onClick={() => openPrescriptionForBooking(selectedBooking)}
                    >
                      <i className="bi bi-file-medical me-2" />
                      {selectedBooking.has_prescription ? 'View prescription' : 'Check prescription'}
                    </button>
                  )}
                  <button className="btn btn-outline-primary w-100 rounded-3 fw-bold" onClick={() => setShowDetailModal(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CANCEL APPOINTMENT MODAL */}
      {showCancelModal && selectedBooking && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>Cancel Appointment
                </h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}></button>
              </div>
              <div className="modal-body p-4">
                <div className="alert alert-warning mb-3">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <strong>Are you sure you want to cancel this appointment?</strong>
                  <p className="mb-0 mt-2 small">This action cannot be undone. The slot will become available for other users to book.</p>
                </div>

                {/* Refund Policy Notice */}
                <div className="alert alert-info mb-3">
                  <i className="bi bi-cash-coin me-2"></i>
                  <strong>Refund Policy:</strong>
                  <p className="mb-0 mt-1 small">
                    If you paid for this appointment, you will receive a refund of the <strong>consultation fee</strong>.
                    <br />
                    <span className="text-muted"><i className="bi bi-info-circle me-1"></i>Note: Processing fee ($5.00) is non-refundable.</span>
                  </p>
                </div>

                <div className="mb-3 p-3 bg-light rounded-3">
                  <div className="row">
                    <div className="col-6">
                      <small className="text-muted d-block">Doctor</small>
                      <strong>{selectedBooking.doctor_name}</strong>
                    </div>
                    <div className="col-6">
                      <small className="text-muted d-block">Date</small>
                      <strong>{formatDate(selectedBooking.date)}</strong>
                    </div>
                    <div className="col-12 mt-2">
                      <small className="text-muted d-block">Time</small>
                      <strong>{formatTime(selectedBooking.start_time)} - {formatTime(selectedBooking.end_time)}</strong>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Reason for Cancellation (Optional)</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Please let us know why you're cancelling..."
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                  ></textarea>
                </div>

                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary flex-fill rounded-3"
                    onClick={() => {
                      setShowCancelModal(false);
                      setCancelReason('');
                    }}
                    disabled={cancelLoading}
                  >
                    Keep Appointment
                  </button>
                  <button
                    className="btn btn-danger flex-fill rounded-3 fw-bold"
                    onClick={handleCancelAppointment}
                    disabled={cancelLoading}
                  >
                    {cancelLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Cancelling...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-x-circle me-2"></i>Confirm Cancel
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RESCHEDULE APPOINTMENT MODAL */}
      {showRescheduleModal && selectedBooking && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-warning">
                  <i className="bi bi-calendar-event me-2"></i>Reschedule Appointment
                </h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowRescheduleModal(false);
                  setRescheduleReason('');
                  setSelectedNewSlot(null);
                  setAvailableSlots([]);
                }}></button>
              </div>
              <div className="modal-body p-4">
                {/* Warning for accepted appointments */}
                {(selectedBooking.status === 'ACCEPTED' || selectedBooking.status === 'CONFIRMED') && (
                  <div className="alert alert-warning mb-4">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    <strong>Note:</strong> Since your appointment was already accepted, rescheduling will require the doctor to accept your new booking request again.
                  </div>
                )}

                {/* Current Appointment Info */}
                <div className="mb-4 p-3 bg-light rounded-3">
                  <h6 className="fw-bold mb-2 text-muted">
                    <i className="bi bi-calendar-x me-2"></i>Current Appointment
                  </h6>
                  <div className="row">
                    <div className="col-md-4">
                      <small className="text-muted d-block">Doctor</small>
                      <strong>{selectedBooking.doctor_name}</strong>
                    </div>
                    <div className="col-md-4">
                      <small className="text-muted d-block">Date</small>
                      <strong>{formatDate(selectedBooking.date)}</strong>
                    </div>
                    <div className="col-md-4">
                      <small className="text-muted d-block">Time</small>
                      <strong>{formatTime(selectedBooking.start_time)} - {formatTime(selectedBooking.end_time)}</strong>
                    </div>
                  </div>
                </div>

                {/* Available Slots */}
                <h6 className="fw-bold mb-3">
                  <i className="bi bi-calendar-check me-2 text-success"></i>Select New Slot
                </h6>

                {slotsLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-2">Loading available slots...</p>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    No available slots found for this doctor. Please try again later.
                  </div>
                ) : (
                  <div className="row g-2 mb-4" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {availableSlots.map((slot) => (
                      <div key={slot.id} className="col-md-6">
                        <div
                          className={`card border-2 cursor-pointer ${selectedNewSlot?.id === slot.id
                            ? 'border-success bg-success bg-opacity-10'
                            : 'border-light hover-border-primary'
                            }`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setSelectedNewSlot(slot)}
                        >
                          <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <div className="fw-bold text-primary">
                                  {formatDate(slot.date)}
                                </div>
                                <div className="text-muted small">
                                  {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                                </div>
                              </div>
                              {selectedNewSlot?.id === slot.id && (
                                <i className="bi bi-check-circle-fill text-success fs-4"></i>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reason */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Reason for Rescheduling (Optional)</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    placeholder="Please let us know why you're rescheduling..."
                    value={rescheduleReason}
                    onChange={(e) => setRescheduleReason(e.target.value)}
                  ></textarea>
                </div>

                {/* Selected New Slot Summary */}
                {selectedNewSlot && (
                  <div className="alert alert-success mb-3">
                    <i className="bi bi-check-circle me-2"></i>
                    <strong>New Appointment:</strong> {formatDate(selectedNewSlot.date)} at {formatTime(selectedNewSlot.start_time)} - {formatTime(selectedNewSlot.end_time)}
                  </div>
                )}

                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary flex-fill rounded-3"
                    onClick={() => {
                      setShowRescheduleModal(false);
                      setRescheduleReason('');
                      setSelectedNewSlot(null);
                      setAvailableSlots([]);
                    }}
                    disabled={rescheduleLoading}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-warning flex-fill rounded-3 fw-bold"
                    onClick={handleRescheduleAppointment}
                    disabled={rescheduleLoading || !selectedNewSlot}
                  >
                    {rescheduleLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Rescheduling...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-calendar-check me-2"></i>Confirm Reschedule
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .hover-shadow {
          transition: all 0.3s ease;
        }
        .hover-shadow:hover {
          transform: translateY(-5px);
          box-shadow: 0 1rem 3rem rgba(0,0,0,.15) !important;
        }
        .hover-border-primary:hover {
          border-color: #0d6efd !important;
        }
        .cursor-pointer {
          cursor: pointer;
        }
      `}</style>

      {viewPrescription && (
        <ViewPrescriptionModal
          prescription={viewPrescription}
          onClose={() => setViewPrescription(null)}
        />
      )}
    </div>
  );
}

/**
 * VideoCallButton Component
 * 
 * Smart button based on appointment timing:
 * - Past (Completed): Shows "Completed" badge
 * - Current (Within slot time): Shows "Join Call" button  
 * - Future: Shows "Scheduled" with time until start
 */
function VideoCallButton({ booking, navigate }) {
  const [status, setStatus] = useState('loading'); // loading, completed, current, future
  const [timeInfo, setTimeInfo] = useState('');

  useEffect(() => {
    const calculateStatus = () => {
      if (!booking.date || !booking.start_time || !booking.end_time) {
        setStatus('current'); // Default to allowing join
        return;
      }

      // Parse date and times
      const slotDate = new Date(booking.date);
      const [startHour, startMin] = booking.start_time.split(':').map(Number);
      const [endHour, endMin] = booking.end_time.split(':').map(Number);

      const startDateTime = new Date(slotDate);
      startDateTime.setHours(startHour, startMin, 0, 0);

      const endDateTime = new Date(slotDate);
      endDateTime.setHours(endHour, endMin, 0, 0);

      const now = new Date();

      // 5 minutes buffer before and 5 min grace period after
      const earlyBuffer = 5 * 60 * 1000; // 5 min before
      // const lateBuffer = 5 * 60 * 1000; // 5 min after (grace period)
      const lateBuffer = 0;
      // no grace period
      const adjustedStart = new Date(startDateTime.getTime() - earlyBuffer);
      const adjustedEnd = new Date(endDateTime.getTime() + lateBuffer);

      if (now > adjustedEnd) {
        // Past - session completed
        setStatus('completed');
      } else if (now >= adjustedStart && now <= adjustedEnd) {
        // Current - can join now
        setStatus('current');
      } else {
        // Future - show scheduled time
        setStatus('future');

        // Calculate time until start
        const diffMs = startDateTime - now;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        if (diffHours > 24) {
          const days = Math.floor(diffHours / 24);
          setTimeInfo(`in ${days} day${days > 1 ? 's' : ''}`);
        } else if (diffHours > 0) {
          setTimeInfo(`in ${diffHours}h ${diffMins}m`);
        } else if (diffMins > 0) {
          setTimeInfo(`in ${diffMins}m`);
        } else {
          setTimeInfo('starting soon');
        }
      }
    };

    calculateStatus();
    const interval = setInterval(calculateStatus, 1000);
    return () => clearInterval(interval);
  }, [booking]);

  const handleClick = () => {
    navigate(`/user/waiting-room/${booking.appointment_id}`);
  };

  // Loading state
  if (status === 'loading') {
    return (
      <span style={{
        padding: '6px 16px',
        backgroundColor: '#9ca3af',
        borderRadius: '20px',
        color: '#fff',
        fontSize: '14px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <i className="bi bi-hourglass-split"></i> Loading...
      </span>
    );
  }

  // Session Expired - past session (missed/not attended)
  if (status === 'completed') {
    return (
      <span style={{
        padding: '6px 16px',
        backgroundColor: '#ef4444',
        borderRadius: '20px',
        color: '#fff',
        fontSize: '14px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <i className="bi bi-x-circle-fill"></i> Session Expired
      </span>
    );
  }

  // Future - scheduled
  if (status === 'future') {
    return (
      <span style={{
        padding: '6px 16px',
        backgroundColor: '#f59e0b',
        borderRadius: '20px',
        color: '#fff',
        fontSize: '14px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <i className="bi bi-clock"></i> Scheduled {timeInfo}
      </span>
    );
  }

  // Current - can join now
  return (
    <button
      onClick={handleClick}
      style={{
        padding: '6px 16px',
        backgroundColor: '#10b981',
        border: 'none',
        borderRadius: '20px',
        color: '#fff',
        fontSize: '14px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        animation: 'pulse 2s infinite'
      }}
    >
      <i className={`bi ${booking.call_type === 'AUDIO' ? 'bi-telephone-fill' : 'bi-camera-video-fill'}`}></i>
      {booking.call_type === 'AUDIO' ? 'Join Audio Call' : 'Join Call'}
    </button>
  );
}

export default UserBookings;
